import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  QueueMusic as QueueIcon,
  PlaylistAdd as AddToPlaylistIcon,
  Lock as LockIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { Track, Album, Artist } from '@/types';
import { useAudio } from '@/context/AudioProvider';
import { PLACEHOLDER_ALBUM } from '@/services/mediaService';
import AddToMixtapeDialog from '@/components/mixtapes/AddToMixtapeDialog';
import { useNavigate } from 'react-router-dom';

// Format duration from milliseconds to MM:SS
const formatDuration = (durationMs?: number): string => {
  if (!durationMs) return '0:00';
  
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

interface TrackListProps {
  tracks: Track[];
  album: Album;
  artist: Artist;
  registerTrackRef?: (id: string, element: HTMLElement | null) => void;
  canPlay?: boolean;
  accessReason?: string;
}

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0.5),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
  },
}));

const TrackList: React.FC<TrackListProps> = ({
  tracks,
  album,
  artist,
  registerTrackRef,
  canPlay = true,
  accessReason,
}) => {
  const { state, playTrack, togglePlay } = useAudio();
  const { isPlaying, currentTrack } = state;
  const theme = useTheme();
  const navigate = useNavigate();
  const [mixtapeDialog, setMixtapeDialog] = useState<{ open: boolean; trackId?: string }>({ open: false });

  const isMobile = useMediaQuery('(max-width:768px)');
  const isTinyScreen = useMediaQuery('(max-width:360px)');

  const albumArtwork = album.artwork_url || PLACEHOLDER_ALBUM;
  const sortedTracks = [...tracks].sort(
    (a, b) => (a.track_number ?? 0) - (b.track_number ?? 0)
  );

  const handlePlayTrack = (track: Track) => {
    if (!canPlay) {
      if (album.access_type === 'free' || album.access_type === 'standard') {
        alert(accessReason || 'Access denied');
      } else {
        navigate(`/checkout/album/${album.id}`);
      }
      return;
    }

    const tracksWithMeta = sortedTracks.map(t => ({
      ...t,
      albumArtwork: album.artwork_url || PLACEHOLDER_ALBUM,
      albumName: album.title,
      artistName: artist?.name,
      artistProfileImage: artist?.image_url,
    }));

    const trackWithMeta = {
      ...track,
      albumArtwork: album.artwork_url || PLACEHOLDER_ALBUM,
      albumName: album.title,
      artistName: artist?.name,
      artistProfileImage: artist?.image_url,
    };

    playTrack(trackWithMeta, tracksWithMeta);
  };

  const addToQueue = (track: Track, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canPlay) {
      if (album.access_type === 'free' || album.access_type === 'standard') {
        alert(accessReason || 'Access denied');
      } else {
        navigate(`/checkout/album/${album.id}`);
      }
      return;
    }

    // Queue functionality would be implemented here
    console.log('Add to queue:', track.title);
  };

  const addToPlaylist = (track: Track, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canPlay) {
      alert(accessReason || 'Access required to add tracks to playlist');
      return;
    }
    setMixtapeDialog({ open: true, trackId: track.id?.toString() });
  };

  if (tracks.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          No tracks available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {!canPlay && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            🔒 {accessReason || 'Premium access required to play tracks'}
          </Typography>
          {album.access_type === 'soundcheck' && (
            <Button
              size="small"
              variant="contained"
              startIcon={<ShoppingCartIcon />}
              onClick={() => navigate(`/checkout/album/${album.id}`)}
              sx={{ fontSize: '0.75rem' }}
            >
              Purchase Soundcheck Access
            </Button>
          )}
        </Box>
      )}

      <List sx={{ p: 0 }}>
        {sortedTracks.map((track, index) => {
          const isCurrentTrack = currentTrack?.id === track.id;
          const isCurrentlyPlaying = isCurrentTrack && isPlaying;

          return (
            <div key={track.id}>
              <StyledListItem
                ref={el => registerTrackRef?.(track.id, el as HTMLLIElement | null)}
                sx={{
                  opacity: canPlay ? 1 : 0.6,
                  cursor: canPlay ? 'pointer' : 'default',
                  backgroundColor: isCurrentTrack ? 'action.selected' : 'transparent'
                }}
              >
                <ListItemButton
                  onClick={() => handlePlayTrack(track)}
                  disabled={!canPlay}
                  sx={{
                    borderRadius: 1,
                    px: isMobile ? 1 : 2,
                    py: 1,
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: isTinyScreen ? 40 : 56 }}>
                    <Avatar
                      src={albumArtwork}
                      alt={album.title}
                      variant="rounded"
                      sx={{
                        width: isTinyScreen ? 32 : 40,
                        height: isTinyScreen ? 32 : 40,
                      }}
                    />
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isCurrentTrack ? 'bold' : 'normal',
                          color: isCurrentTrack ? 'primary.main' : 'text.primary',
                          fontSize: isTinyScreen ? '0.8rem' : '0.875rem',
                        }}
                      >
                        {track.track_number && `${track.track_number}. `}
                        {track.title}
                      </Typography>
                    }
                    secondary={
                      !isTinyScreen && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {track.duration ? formatDuration(track.duration) : 'Unknown duration'}
                        </Typography>
                      )
                    }
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {canPlay ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayTrack(track);
                          }}
                          sx={{ color: isCurrentTrack ? 'primary.main' : 'text.secondary' }}
                        >
                          {isCurrentlyPlaying ? <PauseIcon /> : <PlayIcon />}
                        </IconButton>

                        {!isMobile && (
                          <>
                            <Tooltip title="Add to queue">
                              <IconButton
                                size="small"
                                onClick={(e) => addToQueue(track, e)}
                                sx={{ color: 'text.secondary' }}
                              >
                                <QueueIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Add to playlist">
                              <IconButton
                                size="small"
                                onClick={(e) => addToPlaylist(track, e)}
                                sx={{ color: 'text.secondary' }}
                              >
                                <AddToPlaylistIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </>
                    ) : (
                      <Tooltip title={accessReason || 'Premium access required'}>
                        <IconButton
                          size="small"
                          disabled
                          sx={{ color: 'text.disabled' }}
                        >
                          <LockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ListItemButton>
              </StyledListItem>

              {index < sortedTracks.length - 1 && (
                <Divider variant="inset" component="li" />
              )}
            </div>
          );
        })}
      </List>
      <AddToMixtapeDialog
        open={mixtapeDialog.open}
        trackId={mixtapeDialog.trackId}
        onClose={() => setMixtapeDialog({ open: false })}
      />
    </Box>
  );
};

export default TrackList;
